<template>
  <div class="sv-grid-wrap" :class="{ 'sv-grid-wrap--phone': phone }" data-test="sv-grid">
    <div class="sv-grid" :style="gridStyle">
      <!-- Header row: sticky so the operatory names stay visible while the day scrolls. -->
      <div class="sv-grid__corner"></div>
      <div
        v-for="op in operatories"
        :key="`h-${op.OperatoryNum}`"
        class="sv-grid__ophead"
        :data-test="`sv-ophead-${op.OperatoryNum}`"
      >
        <span class="sv-grid__opname" :title="op.OpName">{{ op.OpName }}</span>
        <span class="sv-grid__provs">
          <span
            v-for="p in providersFor(op.OperatoryNum)"
            :key="`${op.OperatoryNum}-${p.ProvNum}`"
            class="sv-grid__prov"
            :style="{ '--pv': p.color }"
            :title="p.full"
            :data-test="`sv-prov-chip-${op.OperatoryNum}-${p.ProvNum}`"
          >{{ p.initials }}</span>
        </span>
      </div>

      <!-- Time gutter -->
      <div class="sv-grid__gutter" :style="{ height: `${bodyHeight}px` }">
        <div
          v-for="h in hours"
          :key="`t-${h}`"
          class="sv-grid__hour-label"
          :style="{ height: `${hourHeight}px` }"
        >{{ hourLabel(h) }}</div>
      </div>

      <!-- One column per operatory -->
      <div
        v-for="op in operatories"
        :key="`c-${op.OperatoryNum}`"
        class="sv-grid__col"
        :style="{ height: `${bodyHeight}px` }"
        :data-test="`sv-col-${op.OperatoryNum}`"
      >
        <div
          v-for="h in hours"
          :key="`r-${op.OperatoryNum}-${h}`"
          class="sv-grid__hour-row"
          :style="{ height: `${hourHeight}px` }"
        ></div>

        <!--
          The NOW line sits above the hour rows but BELOW the blocks and blockouts: it comes before
          them in the DOM and has no z-index, so a block (white) covers it rather than being struck
          through (Nilay, 2026-10-24: the old 2px brand-green line over the blocks was too loud).
        -->
        <div
          v-if="nowTop !== null"
          class="sv-grid__now"
          :style="{ top: `${nowTop}px` }"
          aria-hidden="true"
        ></div>

        <div
          v-for="b in blockoutsFor(op.OperatoryNum)"
          :key="`b-${b.ScheduleNum}-${op.OperatoryNum}`"
          class="sv-grid__blockout"
          :style="blockoutStyle(b)"
          :title="b.BlockoutName || 'Blockout'"
        >{{ b.BlockoutName }}</div>

        <AppointmentBlock
          v-for="appt in columns[op.OperatoryNum] || []"
          :key="appt.AptNum"
          :appt="appt"
          :tier="tierFor(appt)"
          :view-settings="viewSettings"
          :bucket-map="bucketMap"
          :alert-payload="alertPayload"
          :accent="stripeFor(appt)"
          :top="geometryFor(appt).top"
          :height="geometryFor(appt).height"
          :lane="appt.lane || 0"
          :lane-count="appt.laneCount || 1"
          :checklist="checklists[appt.AptNum] || null"
          :checklist-mode="checklistMode"
          @select="$emit('select-appointment', $event)"
          @card-enter="$emit('card-enter', $event)"
          @card-leave="$emit('card-leave', $event)"
          @alert-focus="$emit('alert-focus', $event)"
          @open-checklist="$emit('open-checklist', $event)"
        />
      </div>
    </div>

    <p v-if="unplaced.length" class="sv-grid__unplaced" data-test="sv-unplaced">
      {{ unplaced.length }}
      {{ unplaced.length === 1 ? 'appointment is' : 'appointments are' }}
      not in a visible operatory (hidden in Open Dental, or no chair assigned) and
      {{ unplaced.length === 1 ? 'is' : 'are' }} not drawn above.
    </p>
  </div>
</template>

<script>
/**
 * The operatory grid: a time gutter plus one column per non-hidden operatory, in Open Dental's own
 * ItemOrder, so the layout reads like the office's OD screen.
 *
 * All the maths lives in src/utils/scheduleView/layout.js — pure, import-free and node-tested. This
 * component only turns the numbers into styles.
 */
import AppointmentBlock from './AppointmentBlock.vue';
import {
  computeDayWindow, groupByOperatory, blockGeometry, densityTier,
  hourRows, nowLineTop, timeToMinutes, hourLabel as formatHourLabel, appointmentProviderNum,
  gridTemplateColumns,
} from '@/utils/scheduleView/layout';
import { indexProviders, providerStripeColor, providerColorHex } from '@/utils/scheduleView/provColor';

// 96px an hour (was 76; Nilay, 2026-10-24: "taller by at least 20%"). 96 makes every 5-minute step
// a whole 8px, so block edges land on whole pixels. Same number as $sv-hour-height (node-test).
const HOUR_HEIGHT = 96;

export default {
  name: 'ScheduleGrid',

  components: { AppointmentBlock },

  props: {
    /** The /schedule-view/day payload, already narrowed by the filters. */
    day: { type: Object, required: true },
    /**
     * The UNFILTERED payload, used to choose the grid's hours and to index the providers (whose
     * Open Dental colours are the blocks' stripes).
     *
     * The day's shape must not change when somebody narrows to one provider: a grid that collapses
     * from 7am–6pm to 9am–11am as you toggle a filter is unreadable, and it hides the fact that the
     * practice works the rest of the day. Defaults to `day` when nothing is filtered.
     */
    unfilteredDay: { type: Object, default: null },
    viewSettings: { type: Object, required: true },
    bucketMap: { type: Object, default: () => ({}) },
    /** The /schedule-view/alerts payload, passed straight through to each block. */
    alertPayload: { type: Object, default: null },
    /** Minutes past midnight in the SITE's timezone, or null when not viewing today. */
    nowMinutes: { type: Number, default: null },
    isToday: { type: Boolean, default: false },
    /**
     * The visit checklists the page built for this day, AptNum → { categories, totals }. Built once
     * at the page from the same alert keys the blocks draw (checklist.js checklistsForDay).
     */
    checklists: { type: Object, default: () => ({}) },
    /** 'hover' | 'pop' | 'sheet', decided by the page's ChecklistLayer. */
    checklistMode: { type: String, default: 'hover' },
    /**
     * A phone (the "Columns" view): the columns may be narrower (layout.js GRID_OP_COL_MIN_PHONE) so
     * about two chairs show across the screen while the rest scroll sideways. The page's phone
     * breakpoint, decided in JS like the view itself.
     */
    phone: { type: Boolean, default: false },
  },

  data() {
    return { hourHeight: HOUR_HEIGHT };
  },

  computed: {
    operatories() {
      return this.day.operatories || [];
    },

    window() {
      const source = this.unfilteredDay || this.day;
      return computeDayWindow({
        appointments: source.appointments,
        providerHours: source.providerHours,
        blockouts: this.viewSettings.showBlockouts ? source.blockouts : [],
      });
    },

    hours() {
      return hourRows(this.window.startHour, this.window.endHour);
    },

    bodyHeight() {
      return this.hours.length * this.hourHeight;
    },

    grouped() {
      return groupByOperatory(this.day.appointments || [], this.operatories);
    },

    columns() {
      return this.grouped.columns;
    },

    unplaced() {
      return this.grouped.unplaced;
    },

    gridStyle() {
      return { gridTemplateColumns: gridTemplateColumns(this.operatories.length, this.phone) };
    },

    nowTop() {
      return nowLineTop({
        isToday: this.isToday,
        nowMinutes: this.nowMinutes,
        startHour: this.window.startHour,
        endHour: this.window.endHour,
        hourHeight: this.hourHeight,
      });
    },

    /**
     * ProvNum -> provider, for the header chips and the stripe colour.
     *
     * From the UNFILTERED day when there is one: the roster does not change because somebody
     * narrowed the view, and a stripe must not change colour (or go grey) with the filter.
     */
    providerIndex() {
      return indexProviders((this.unfilteredDay || this.day).providers);
    },
  },

  methods: {
    /**
     * The block's left stripe: the appointment's provider's Open Dental colour (the hygienist on a
     * hygiene visit), or neutral grey when there is none. Never the chair's — columns are white.
     */
    stripeFor(appt) {
      return providerStripeColor(appt, this.providerIndex, { appointmentProviderNum });
    },

    hourLabel(h) {
      return formatHourLabel(h);
    },

    geometryFor(appt) {
      return blockGeometry(appt, {
        startHour: this.window.startHour,
        hourHeight: this.hourHeight,
      }) || { top: 0, height: 0 };
    },

    tierFor(appt) {
      return densityTier(this.geometryFor(appt).height);
    },

    /**
     * Who is working this chair today, derived from the day's own appointments rather than from
     * provider hours: OD's schedule rows are per provider, not per operatory, so they cannot say
     * which chair somebody is in. The appointments can, and they are the truth the office sees.
     */
    providersFor(operatoryNum) {
      const seen = [];
      const out = [];
      (this.columns[operatoryNum] || []).forEach((appt) => {
        [appt.ProvNum, appt.ProvHyg].forEach((provNum) => {
          if (!provNum || seen.indexOf(provNum) !== -1) return;
          const p = this.providerIndex[provNum];
          if (!p) return;
          seen.push(provNum);
          out.push({
            ProvNum: provNum,
            initials: p.Abbr || `${(p.FName || '?')[0]}${(p.LName || '')[0] || ''}`,
            full: `${p.FName || ''} ${p.LName || ''}`.trim(),
            // The dot on the chip is the very colour that provider's blocks have for a stripe.
            color: providerColorHex(p),
          });
        });
      });
      return out.slice(0, 3);
    },

    blockoutsFor(operatoryNum) {
      if (!this.viewSettings.showBlockouts) return [];
      // A blockout with no scheduleop row covers EVERY operatory — Open Dental permits that, and
      // dropping it would hide a whole-practice block (lunch, a staff meeting).
      return (this.day.blockouts || []).filter(
        b => b.OperatoryNum === operatoryNum || b.OperatoryNum === null || b.OperatoryNum === undefined
      );
    },

    blockoutStyle(b) {
      const start = timeToMinutes(b.StartTime);
      const end = timeToMinutes(b.StopTime);
      if (start === null || end === null) return { display: 'none' };
      const perMinute = this.hourHeight / 60;
      return {
        top: `${(start - this.window.startHour * 60) * perMinute}px`,
        height: `${Math.max(8, (end - start) * perMinute)}px`,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../tokens';

// White, a 1px line, 14px corners — the Forms Settings card. The wrap is the horizontal scroller: when
// the columns are wider than the screen (a phone's Columns view, an iPad with seven chairs) it scrolls
// sideways, with the time gutter and the chair header sticky inside it.
.sv-grid-wrap {
  overflow: auto;
  background: $sv-surface;
  border: 1px solid $sv-line;
  border-radius: $sv-radius-lg;
}

// ⛔ A phone's Columns view must ALSO scroll down inside the wrap. `position: sticky` sticks to the
// nearest scrolling ancestor, and `overflow-x: auto` makes the wrap one (CSS forces overflow-y to auto
// with it) — so a wrap as tall as its content never scrolls vertically, the PAGE does, and the chair
// header scrolled away with it (found on a real 390px phone, 2026-10-23: header top -29px after a
// page scroll). Capped to the screen between the app's 60px top bar and 56px bottom tab bar (plus a
// little air), the wrap becomes the vertical scroller too, and the header and gutter stay put whichever
// way it moves. 100dvh follows a phone's collapsing browser toolbar; an older browser ignores that line
// and keeps the vh one. Phone only: an iPad / desktop keeps the page scroll it has always had.
.sv-grid-wrap--phone {
  max-height: calc(100vh - 140px);
  max-height: calc(100dvh - 140px);
}

.sv-grid {
  display: grid;
  min-width: max-content;
}

.sv-grid__corner {
  position: sticky;
  top: 0;
  left: 0;
  z-index: 5;
  background: $sv-surface;
  border-bottom: 1px solid $sv-line;
}

.sv-grid__ophead {
  position: sticky;
  top: 0;
  z-index: 4;
  background: $sv-surface;
  border-bottom: 1px solid $sv-line;
  border-left: 1px solid $sv-line;
  padding: 11px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  min-width: 0;
}

.sv-grid__opname {
  font-size: 14px;
  font-weight: 700;
  color: $sv-ink;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sv-grid__provs {
  display: flex;
  flex: 0 0 auto;
}

// Who works the chair: a white pill with a 1px inset line, a 7px dot in THAT provider's stripe colour
// (the very colour their blocks have, provColor.js) and their initials. The chip names the provider,
// the dot says which stripe is theirs — the chair itself has no colour.
.sv-grid__prov {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 22px;
  margin-left: 4px;
  padding: 0 7px 0 6px;
  border-radius: $sv-radius-pill;
  background: $sv-surface;
  box-shadow: inset 0 0 0 1px $sv-line;
  color: $sv-ink-2;
  font-size: 10.5px;
  font-weight: 700;
  line-height: 1;
  text-transform: uppercase;

  &::before {
    content: '';
    flex: 0 0 auto;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: var(--pv, #{$sv-ink-4});
  }
}

// ⛔ z-index 4, ABOVE everything a block draws (its alert row is 2, a hovered block 3): when the grid is
// scrolled sideways the blocks slide under this sticky gutter, and their chips and icons must go
// under it — at 2 they painted over the hour labels on a phone (found 2026-10-23).
.sv-grid__gutter {
  position: sticky;
  left: 0;
  z-index: 4;
  background: $sv-surface;
}

// The hour, in Hanken at 11.5px, muted; the hairline continues across the columns at the same weight.
.sv-grid__hour-label {
  box-sizing: border-box;
  padding: 4px 8px 0 0;
  text-align: right;
  font-size: 11.5px;
  font-weight: 500;
  color: $sv-ink-3;
  border-top: 1px solid $sv-line-2;
}

// White, with the 1px separator on the left — no per-chair wash (Nilay, 2026-10-23).
.sv-grid__col {
  position: relative;
  background: $sv-surface;
  border-left: 1px solid $sv-line;
}

.sv-grid__hour-row {
  border-top: 1px solid $sv-line-2;
}

// Lunch, a staff meeting: the design's hatched break block — two near-white greens, a strong line.
.sv-grid__blockout {
  position: absolute;
  left: 3px;
  right: 3px;
  z-index: 1;
  box-sizing: border-box;
  border: 1px solid $sv-line-strong;
  border-radius: $sv-radius;
  padding: 0 7px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: $sv-ink-3;
  overflow: hidden;
  background: repeating-linear-gradient(
    135deg,
    #f7faf9 0 6px,
    #f0f3f2 6px 12px
  );
}

// A 1px soft coral hairline, not the brand green (Nilay, 2026-10-24). No z-index on purpose — see the
// template: the blocks paint over it.
.sv-grid__now {
  position: absolute;
  left: 0;
  right: 0;
  height: 1px;
  background: $sv-now;
  pointer-events: none;
}

.sv-grid__unplaced {
  margin: 0;
  padding: 8px 12px;
  font-size: 12px;
  color: $sv-ink-3;
  border-top: 1px solid $sv-line;
  background: $sv-surface;
}

@media (max-width: 768px) {
  .sv-grid__ophead { padding: 8px 8px; }
  .sv-grid__opname { font-size: 13px; }
}
</style>
